import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { toast } from '@ooh/ui/components/sonner';
import { AlertTriangle, Check, Download, Minus, XCircle } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { StatCard } from '@ooh/ui/components/stat-card';
import { availabilityImportEndpoints } from '@ooh/contracts';
import type { AvailabilityImportStatusResponse } from '@ooh/contracts';
import { useApiClient } from '../../plugs/api-client';
import { useApiSuspenseQuery } from '../../data/use-api-suspense-query';
import { QueryBoundary } from '../../layouts/query-boundary';
import { SectionSkeleton } from '../../layouts/skeletons';
import { triggerErrorReportDownload } from '../../lib/error-report-csv';
import { refetchIntervalFn } from '../../lib/import-polling';

// Re-exported for unit testing (existing tests import it from this module).
export { refetchIntervalFn };

interface ResultPhaseProps {
  importJobId: number;
  onFinish: () => void;
  onRetry: () => void;
}

interface SuccessResultProps {
  t: TFunction<'availabilityImport'>;
  job: AvailabilityImportStatusResponse;
  isDownloading: boolean;
  onDownload: () => void;
  onFinish: () => void;
}

function SuccessResult({ t, job, isDownloading, onDownload, onFinish }: SuccessResultProps) {
  return (
    <>
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <Check className="h-8 w-8 text-emerald-600" />
        </div>
        <div>
          <p className="text-[17px] font-semibold">{t('result.headingSuccess')}</p>
          <p className="text-[13px] text-muted-foreground mt-1">{t('result.subtitleSuccess')}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3" data-testid="result-stats">
        <StatCard
          icon={Check}
          label={t('result.statUpdated')}
          value={job.updatedRows ?? 0}
          loading={false}
          tone="success"
          testId="stat-updated"
        />
        <StatCard
          icon={Minus}
          label={t('result.statUnchanged')}
          value={job.unchangedRows ?? 0}
          loading={false}
          tone="neutral"
          testId="stat-unchanged"
        />
        <StatCard
          icon={AlertTriangle}
          label={t('result.statUnmatched')}
          value={job.unmatchedRows ?? 0}
          loading={false}
          tone="warning"
          testId="stat-unmatched"
        />
      </div>

      <Button
        variant="outline"
        type="button"
        fullWidth
        data-testid="download-unmatched-report"
        disabled={isDownloading}
        onClick={onDownload}
      >
        <Download className="h-4 w-4" />
        {t('result.downloadUnmatchedReport')}
      </Button>

      <Button type="button" fullWidth onClick={onFinish} data-testid="finish-button">
        {t('result.finish')}
      </Button>
    </>
  );
}

interface FailureResultProps {
  t: TFunction<'availabilityImport'>;
  errorMessage: string | null;
  onRetry: () => void;
}

function FailureResult({ t, errorMessage, onRetry }: FailureResultProps) {
  return (
    <div data-testid="phase-result-failure">
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
          <XCircle className="h-8 w-8 text-red-600" />
        </div>
        <div>
          <p className="text-[17px] font-semibold text-red-700">{t('result.headingFailure')}</p>
          <p className="text-[13px] text-muted-foreground mt-1">{errorMessage}</p>
        </div>
      </div>

      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 flex items-start gap-2 mt-4">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>{t('result.failureNote')}</span>
      </div>

      <Button type="button" fullWidth onClick={onRetry} data-testid="retry-button" className="mt-4">
        {t('result.retry')}
      </Button>
    </div>
  );
}

/**
 * Result phase: polls `GET /availability-import/:id` until the job reaches a
 * terminal status, then renders either the three result-bucket counts (with
 * an unmatched-rows CSV download) or a distinct failure state — never a
 * false 0/0/0 success render for a failed job.
 *
 * Traceability: availability-import-ui / Result phase with downloadable
 * unmatched report.
 */
export function ResultPhase(props: ResultPhaseProps) {
  return (
    <QueryBoundary fallback={<SectionSkeleton />}>
      <ResultPhaseContent {...props} />
    </QueryBoundary>
  );
}

function ResultPhaseContent({ importJobId, onFinish, onRetry }: ResultPhaseProps) {
  const { t } = useTranslation('availabilityImport');
  const api = useApiClient();
  const [isDownloading, setIsDownloading] = React.useState(false);

  const { data: job } = useApiSuspenseQuery(
    availabilityImportEndpoints.status,
    { id: importJobId },
    { refetchInterval: (data) => refetchIntervalFn(data?.status) },
  );

  const handleDownload = React.useCallback(async () => {
    setIsDownloading(true);
    try {
      const response = await api.call(availabilityImportEndpoints.errors, { id: importJobId });
      triggerErrorReportDownload(importJobId, response.errors, {
        row: t('result.errorReportColumnRow'),
        grwId: t('result.errorReportColumnGrwId'),
        field: t('result.errorReportColumnField'),
        message: t('result.errorReportColumnMessage'),
        severity: t('result.errorReportColumnSeverity'),
        identifiers: {
          supplierNumber: t('result.errorReportColumnSupplierNumber'),
          supplierName: t('result.errorReportColumnSupplierName'),
          latitude: t('result.errorReportColumnLatitude'),
          longitude: t('result.errorReportColumnLongitude'),
          address: t('result.errorReportColumnAddress'),
          city: t('result.errorReportColumnCity'),
        },
      });
    } catch (error) {
      console.error('Failed to download unmatched-rows report', { importJobId, error });
      toast.error(t('result.downloadReportFailed'));
    } finally {
      setIsDownloading(false);
    }
  }, [api, importJobId, t]);

  const isTerminal = job.status === 'completed' || job.status === 'failed';

  if (!isTerminal) {
    return <p data-testid="phase-result-loading">{t('result.polling')}</p>;
  }

  return (
    <div className="max-w-xl mx-auto space-y-6 py-6" data-testid="phase-result">
      {job.status === 'failed' ? (
        <FailureResult t={t} errorMessage={job.errorMessage} onRetry={onRetry} />
      ) : (
        <SuccessResult
          t={t}
          job={job}
          isDownloading={isDownloading}
          onDownload={() => {
            void handleDownload();
          }}
          onFinish={onFinish}
        />
      )}
    </div>
  );
}
